import { cn } from '@/lib/utils';

interface ProgressDonutProps {
  value: number;
  size?: number;
  strokeWidth?: number;
  className?: string;
  showLabel?: boolean;
  labelClassName?: string;
  trackColor?: string;
  progressColor?: string;
  segments?: { value: number; color: string; label?: string }[];
}

export default function ProgressDonut({
  value,
  size = 80,
  strokeWidth = 8,
  className,
  showLabel = true,
  labelClassName,
  trackColor = 'hsl(var(--muted))',
  progressColor = 'hsl(var(--primary))',
  segments,
}: ProgressDonutProps) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  // Single progress mode
  if (!segments) {
    const progress = Math.min(100, Math.max(0, value));
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <div className={cn('relative inline-flex items-center justify-center', className)}>
        <svg
          width={size}
          height={size}
          className="transform -rotate-90"
        >
          {/* Background track */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={trackColor}
            strokeWidth={strokeWidth}
          />
          {/* Progress arc */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={progressColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-500 ease-out"
          />
        </svg>
        {showLabel && (
          <div className={cn(
            'absolute inset-0 flex items-center justify-center',
            labelClassName
          )}>
            <span className="text-sm font-bold text-foreground">
              {Math.round(progress)}%
            </span>
          </div>
        )}
      </div>
    );
  }

  // Multi-segment mode (for success/warning/failure breakdown)
  const total = segments.reduce((sum, seg) => sum + seg.value, 0);
  let currentOffset = 0;

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={strokeWidth}
        />
        {/* Segments */}
        {segments.map((segment, index) => {
          const segmentPercent = total > 0 ? (segment.value / total) * 100 : 0;
          const segmentLength = (segmentPercent / 100) * circumference;
          const offset = circumference - currentOffset;
          currentOffset += segmentLength;

          return (
            <circle
              key={index}
              cx={center}
              cy={center}
              r={radius}
              fill="none"
              stroke={segment.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${segmentLength} ${circumference - segmentLength}`}
              strokeDashoffset={offset}
              className="transition-all duration-500 ease-out"
            />
          );
        })}
      </svg>
      {showLabel && (
        <div className={cn(
          'absolute inset-0 flex flex-col items-center justify-center',
          labelClassName
        )}>
          <span className="text-lg font-bold text-foreground">{total}</span>
          <span className="text-[10px] text-muted-foreground">total</span>
        </div>
      )}
    </div>
  );
}
